import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Website Components
import TopBar from './components/TopBar';
import Header from './components/Header';
import Hero from './components/Hero';
import TopRoles from './components/TopRoles';
import CompaniesAndVideo from './components/CompaniesAndVideo';
import RecentJobs from './components/RecentJobs';
import WhyUsAndTestimonials from './components/WhyUsAndTestimonials';
import Footer from './components/Footer';
import FloatingButtons from "./components/FloatingButtons";
import ManageEmployers from './admin/pages/ManageEmployers';
import EmployerProfile from './components/EmployerProfile';
import EmployerLogin from './components/EmployerLogin';
import VideoGallery from './components/VideoGallery';

// Admin Pages
import JobView from "./admin/pages/JobView";
import Login from "./admin/pages/Login";
import Dashboard from "./admin/pages/Dashboard";
import Candidates from "./admin/pages/Candidates";
import ProtectedRoute from "./admin/components/ProtectedRoute";
import CandidateProtectedRoute from "./admin/components/CandidateProtectedRoute"; 
import ResetPassword from "./admin/pages/ResetPassword";
import ForgotPassword from "./admin/pages/ForgotPassword";
import AddJob from './admin/pages/AddJob';
import Jobs from './admin/pages/Jobs';
import EditJob from './admin/pages/EditJob';
import AddCandidate from './admin/pages/AddCandidate';
import EditCandidate from './admin/pages/EditCandidate';
import Employees from './admin/pages/Employees';
import AddEmployee from './admin/pages/AddEmployee';
import Roles from './admin/pages/Roles';
import { Toaster } from 'react-hot-toast';
import Profile from "./admin/pages/Profile";
import Settings from './admin/pages/Settings'; 
import Categories from './admin/pages/Categories';
import JobListing from './components/JobListing';
import CandidateProfile from "./components/CandidateProfile";
import JobDetails from './components/JobDetails';

import AdminTopCompanies from './admin/components/AddTopCompany';
import AdminTestimonials from './admin/pages/AdminTestimonials';

import CandidateDetails from "./admin/pages/CandidateDetails";
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';

import ManagePartners from './admin/pages/ManagePartners';
import EmployerView from './admin/pages/EmployerView';
import AdminBannerManager from './admin/pages/AdminBannerManager'; // 🌟 Sahi Path

// Payment Approvals & Videos
import PaymentApprovals from './admin/pages/PaymentApprovals';
import Videos from './admin/pages/Videos'; 
import AddVideo from './admin/pages/AddVideo';

const Home = ({ theme, toggleTheme }) => (
  <div className="min-h-screen bg-white text-gray-900 dark:bg-background dark:text-foreground transition-colors duration-300">
    <div className="animate-section" style={{"--delay": "0ms"}}><TopBar /></div>
    
    <div className="animate-section relative z-[999]" style={{"--delay": "80ms"}}>
      <Header theme={theme} toggleTheme={toggleTheme} />
    </div>
    
    <div className="animate-section relative z-0" style={{"--delay": "160ms"}}>
      <Hero />
    </div>
    
    <div className="animate-section" style={{"--delay": "300ms"}}><TopRoles /></div>
    <div className="animate-section" style={{"--delay": "450ms"}}><CompaniesAndVideo /></div>
    <div className="animate-section" style={{"--delay": "525ms"}}><VideoGallery /></div>
    <div className="animate-section" style={{"--delay": "600ms"}}><RecentJobs /></div>
    <div className="animate-section" style={{"--delay": "750ms"}}><WhyUsAndTestimonials /></div>
    <div className="animate-section" style={{"--delay": "900ms"}}><Footer /></div>
    <FloatingButtons />
  </div>
);

function App() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
  };

  return (
    <BrowserRouter>
      <Toaster
        position="top-right" 
        toastOptions={{
          duration: 3000,
          style: {
            background: '#333',
            color: '#fff',
          },
        }} 
      />

      <Routes>
        <Route path="/" element={<Home theme={theme} toggleTheme={toggleTheme} />} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} /> 
        <Route path="/admin/login" element={<Login />} /> 
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />

        {/* ADMIN ROUTES (All secured with ProtectedRoute) */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        
        {/* Jobs */}
        <Route path="/jobs/view/:id" element={<JobView />} />
        <Route path="/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
        <Route path="/jobs/new" element={<ProtectedRoute><AddJob /></ProtectedRoute>} />
        <Route path="/jobs/edit/:id" element={<ProtectedRoute><EditJob /></ProtectedRoute>} />
        <Route path="/jobs/active" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
        <Route path="/jobs/expired" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
        
        {/* Candidates */}
        <Route path="/candidates" element={<ProtectedRoute><Candidates /></ProtectedRoute>} />
        <Route path="/candidates/active" element={<ProtectedRoute><Candidates /></ProtectedRoute>} />
        <Route path="/candidates/shortlisted" element={<ProtectedRoute><Candidates /></ProtectedRoute>} />
        <Route path="/candidates/rejected" element={<ProtectedRoute><Candidates /></ProtectedRoute>} />
        <Route path="/candidates/new" element={<ProtectedRoute><AddCandidate /></ProtectedRoute>} />
        <Route path="/candidates/edit/:id" element={<ProtectedRoute><EditCandidate /></ProtectedRoute>} />
        <Route path="/candidates/view/:id" element={<ProtectedRoute><CandidateDetails /></ProtectedRoute>} />

        {/* Team/Employees */}
        <Route path="/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
        <Route path="/employees/new" element={<ProtectedRoute><AddEmployee /></ProtectedRoute>} />
        
        {/* Settings, Profile, Categories & Admin Features */}
        <Route path="/roles" element={<ProtectedRoute><Roles /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/categories-list" element={<ProtectedRoute><Categories /></ProtectedRoute>} />
        <Route path="/admin/top-companies" element={<ProtectedRoute><AdminTopCompanies /></ProtectedRoute>} />
        <Route path="/admin/testimonials" element={<ProtectedRoute><AdminTestimonials /></ProtectedRoute>} />
        <Route path="/admin/partners" element={<ProtectedRoute><ManagePartners /></ProtectedRoute>} />
        <Route path="/admin/employers/:id" element={<ProtectedRoute><EmployerView /></ProtectedRoute>} />

        {/* Payment Approvals Route */}
        <Route path="/admin/payment-approvals" element={<ProtectedRoute><PaymentApprovals /></ProtectedRoute>} />

        {/* Videos */}
        <Route path="/videos" element={<ProtectedRoute><Videos /></ProtectedRoute>} />
        <Route path="/videos/new" element={<ProtectedRoute><AddVideo /></ProtectedRoute>} />

        {/* Banner Manager Route (Secured) */}
        <Route path="/admin/banner-manager" element={<ProtectedRoute><AdminBannerManager /></ProtectedRoute>} />

        {/* Public / Candidate Routes */}
        <Route path="/job-listing" element={<JobListing />} />
        <Route path="/job-details/:id" element={<JobDetails />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/admin/employers" element={<ManageEmployers />} />
        <Route path="/terms-of-service" element={<TermsAndConditions />} />

        {/* Employer Routes */}
        <Route path="/employer/login" element={<EmployerLogin />} />
        <Route path="/employer/profile" element={<EmployerProfile />} />

        <Route
          path="/candidate-profile"
          element={
            <CandidateProtectedRoute>
              <CandidateProfile />
            </CandidateProtectedRoute>
          }
        />

        {/* Fallback (404) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;